import {
  JSXElementConstructor,
  Key,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";

const links: {
  id: string;
  title: string;
  description: string;
  url: string;
  category: string;
  tags: string[];
  level: "Beginner" | "Intermediate" | "Advanced" | "All";
  readingTime?: number | null;
  source: string;
  dateAdded: string;
}[] = [
  {
    id: "ml-fastai",
    title: "Practical Deep Learning for Coders",
    description:
      "Hands-on course by fast.ai to get you building state-of-the-art models quickly.",
    url: "https://course.fast.ai/",
    category: "Machine Learning",
    tags: ["Deep Learning", "Notebooks", "Applications"],
    level: "Intermediate",
    readingTime: 20,
    source: "fast.ai",
    dateAdded: "2024-11-01",
  },
  {
    id: "ml-3b1b",
    title: "Neural Networks: 3Blue1Brown",
    description: "Visual intuition for neural nets and gradient descent.",
    url: "https://www.3blue1brown.com/topics/neural-networks",
    category: "Machine Learning",
    tags: ["Math", "Visual", "Foundations"],
    level: "Beginner",
    readingTime: 15,
    source: "3Blue1Brown",
    dateAdded: "2023-09-12",
  },
  {
    id: "ml-hf-course",
    title: "Hugging Face NLP Course",
    description:
      "Transformers, tokenizers, and fine-tuning with real projects.",
    url: "https://huggingface.co/learn/nlp-course",
    category: "Machine Learning",
    tags: ["NLP", "Transformers", "PyTorch", "Fine Tuning"],
    level: "Intermediate",
    readingTime: 30,
    source: "Hugging Face",
    dateAdded: "2024-03-22",
  },
  {
    id: "ds-sql",
    title: "Mode Analytics SQL Tutorial",
    description: "A friendly path to real-world SQL for analysts and devs.",
    url: "https://mode.com/sql-tutorial/",
    category: "Data",
    tags: ["SQL", "Analytics", "Basics"],
    level: "Beginner",
    readingTime: 25,
    source: "Mode",
    dateAdded: "2022-06-01",
  },
  {
    id: "web-mdn",
    title: "MDN Web Docs",
    description: "The official reference for HTML, CSS, and JavaScript.",
    url: "https://developer.mozilla.org/",
    category: "Web",
    tags: ["JavaScript", "CSS", "Reference"],
    level: "All",
    readingTime: 5,
    source: "MDN",
    dateAdded: "2021-01-01",
  },
  {
    id: "sys-aws",
    title: "AWS Well-Architected Framework",
    description: "Pillars and best practices for resilient cloud systems.",
    url: "https://aws.amazon.com/architecture/well-architected/",
    category: "Systems",
    tags: ["Cloud", "Best Practices", "Architecture"],
    level: "Advanced",
    readingTime: 35,
    source: "AWS",
    dateAdded: "2024-07-10",
  },
  {
    id: "google-learning",
    title: "Google Machine Learning Education",
    description:
      "Learn to build ML products with Google's Machine Learning Courses",
    category: "Machine Learning",
    tags: ["Best Practices", "Analytics", "Deep Learning"],
    level: "Beginner",
    readingTime: null,
    source: "Google",
    dateAdded: "2025-10-05",
    url: "https://developers.google.com/machine-learning/",
  },
  {
    id: "aws-model-distillation",
    title: "AWS Bedrock",
    description: "Train models for your use case",
    category: "Machine Learning",
    tags: ["Tools", "Fine Tuning"],
    level: "Intermediate",
    readingTime: null,
    source: "AWS",
    dateAdded: "2025-10-05",
    url: "https://aws.amazon.com/bedrock/model-distillation/",
  },
];

// Utility
const uniq = (arr: Iterable<unknown> | null | undefined) =>
  Array.from(new Set(arr));
const formatDate = (iso: string) => new Date(iso).toLocaleDateString();

export default function LearningLinksPage() {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeLevel, setActiveLevel] = useState("All");
  const [selectedTags, setSelectedTags] = useState(new Set());
  const [sort, setSort] = useState("Newest");
  const [favorites, setFavorites] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("learning:favs") || "[]"));
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    localStorage.setItem(
      "learning:favs",
      JSON.stringify(Array.from(favorites))
    );
  }, [favorites]);

  const allCategories = useMemo(
    () => ["All", ...uniq(links.map((l) => l.category))],
    []
  );

  const allLevels = ["All", "Beginner", "Intermediate", "Advanced"];
  const allTags = useMemo(() => uniq(links.flatMap((l) => l.tags)).sort(), []);

  const filtered = useMemo(() => {
    let list = [...links];

    if (activeCategory !== "All")
      list = list.filter((l) => l.category === activeCategory);
    if (activeLevel !== "All")
      list = list.filter((l) => l.level === activeLevel);

    if (selectedTags.size > 0) {
      list = list.filter((l) => l.tags.some((t) => selectedTags.has(t)));
    }

    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (l) =>
          l.title.toLowerCase().includes(q) ||
          l.description.toLowerCase().includes(q) ||
          l.tags.join(" ").toLowerCase().includes(q) ||
          l.source.toLowerCase().includes(q)
      );
    }

    if (sort === "A–Z") list.sort((a, b) => a.title.localeCompare(b.title));
    else if (sort === "Z–A")
      list.sort((a, b) => b.title.localeCompare(a.title));
    else if (sort === "Newest") {
      list.sort(
        (a, b) =>
          new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime()
      );
    } else if (sort === "Oldest")
      list.sort(
        (a, b) =>
          new Date(a.dateAdded).getTime() - new Date(b.dateAdded).getTime()
      );
    else if (sort === "Favorites")
      list.sort(
        (a, b) => (favorites.has(b.id) ? 1 : 0) - (favorites.has(a.id) ? 1 : 0)
      );

    return list;
  }, [activeCategory, activeLevel, favorites, query, selectedTags, sort]);

  const toggleTag = (tag: unknown) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      next.has(tag) ? next.delete(tag) : next.add(tag);
      return next;
    });
  };

  const clearFilters = () => {
    setQuery("");
    setActiveCategory("All");
    setActiveLevel("All");
    setSelectedTags(new Set());
    setSort("Newest");
  };

  const toggleFavorite = (id: unknown) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="container contact-form-section rounded p-4">
      {/* Inline flair for hover/modern feel */}
      <style>{`
        .card-hover { transition: transform .15s ease, box-shadow .15s ease; }
        .card-hover:hover { transform: translateY(-2px); box-shadow: 0 .5rem 1rem rgba(0,0,0,.15); }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .sticky-top-2 { position: sticky; top: .75rem; z-index: 1010; }
      `}</style>

      {/* Header */}
      <div className="mb-4 mb-md-5">
        <div className="d-flex align-items-center gap-3">
          <div>
            <h1 className="h3 mb-1 ">Learning Hub</h1>
            <p className="text-body-secondary mb-0">
              Curated links across ML, data, web, and systems. Filter, search,
              and bookmark your favorites.
            </p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="row g-3 align-items-center mb-3">
        <div className="col-12 col-lg-5">
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="search"
              className="form-control"
              placeholder="Search titles, tags, sources…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <select
            className="form-select"
            value={activeCategory}
            onChange={(e) => setActiveCategory(e.target.value)}
          >
            {allCategories.map((c) => (
              <option key={String(c)} value={String(c)}>
                {String(c)}
              </option>
            ))}
          </select>
        </div>
        <div className="col-12 col-sm-6 col-lg-2">
          <select
            className="form-select"
            value={activeLevel}
            onChange={(e) => setActiveLevel(e.target.value)}
          >
            {allLevels.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <div className="col-12 col-lg-2">
          <select
            className="form-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            {["Newest", "Oldest", "A–Z", "Z–A", "Favorites"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tags */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {allTags.map((t) => {
          const active = selectedTags.has(t);
          return (
            <button
              key={String(t)}
              type="button"
              className={`btn btn-sm ${
                active ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => toggleTag(t)}
            >
              {String(t)}
            </button>
          );
        })}
        <div className="ms-auto d-flex gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={clearFilters}
          >
            Clear
          </button>
          <span className="badge text-bg-light align-self-center">
            {filtered.length} results
          </span>
        </div>
      </div>

      <div className="row g-3 g-md-4">
        {/* Sidebar (optional quick categories) */}
        <aside className="col-12 col-lg-3 order-lg-1">
          <div className="sticky-top-2">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h2 className="h6 mb-0">Categories</h2>
                  <span className="badge rounded-pill text-bg-primary">
                    {allCategories.length - 1}
                  </span>
                </div>
                <div className="list-group list-group-flush">
                  {allCategories.map((c) => (
                    <button
                      key={String(c)}
                      className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                        activeCategory === c ? "active" : ""
                      }`}
                      onClick={() => setActiveCategory(String(c))}
                    >
                      <span>{String(c)}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm mt-3">
              <div className="card-body">
                <h2 className="h6">Quick Tips</h2>
                <ul className="small text-body-secondary mb-0 ps-3">
                  <li>
                    Use <strong>Search</strong> to match titles, tags, and
                    sources.
                  </li>
                  <li>
                    Click <strong>☆</strong> on a card to save favorites
                    (local).
                  </li>
                  <li>
                    Sort by <strong>Newest</strong> or your{" "}
                    <strong>Favorites</strong>.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </aside>

        {/* Results grid */}
        <section className="col-12 col-lg-9 order-lg-2">
          {filtered.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="row g-3 g-md-4">
              {filtered.map((link) => (
                <div className="col-12 col-sm-6" key={link.id}>
                  <LinkCard
                    link={link}
                    isFavorite={favorites.has(link.id)}
                    onToggleFavorite={() => toggleFavorite(link.id)}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function LinkCard({
  link,
  isFavorite,
  onToggleFavorite,
}: {
  link: any;
  isFavorite: any;
  onToggleFavorite: any;
}) {
  return (
    <div className="card h-100 border-0 shadow-sm card-hover">
      <div className="card-body d-flex flex-column">
        <div className="d-flex gap-2 align-items-start mb-2">
          <span className="badge text-bg-primary-subtle text-primary-emphasis">
            {link.category}
          </span>
          {link.level !== "All" && (
            <span className="badge text-bg-secondary-subtle text-secondary-emphasis">
              {link.level}
            </span>
          )}
          <button
            className={`btn btn-sm ms-auto ${
              isFavorite ? "btn-warning" : "btn-outline-secondary"
            }`}
            title={isFavorite ? "Remove from favorites" : "Add to favorites"}
            onClick={onToggleFavorite}
          >
            {isFavorite ? "★" : "☆"}
          </button>
        </div>

        <h3 className="h5 mb-1">{link.title}</h3>
        <p className="text-body-secondary mb-2 line-clamp-2">
          {link.description}
        </p>

        <div className="d-flex flex-wrap gap-2 mb-3">
          {link.tags.map(
            (
              t:
                | boolean
                | ReactElement<any, string | JSXElementConstructor<any>>
                | Iterable<ReactNode>
                | Key
                | null
                | undefined
            ) => (
              <span
                key={String(t)}
                className="badge rounded-pill text-bg-light"
              >
                {String(t)}
              </span>
            )
          )}
        </div>

        <div className="mt-auto d-flex align-items-center justify-content-between">
          <small className="text-body-secondary">
            {link.source} · Added {formatDate(link.dateAdded)}
            {link.readingTime ? ` · ~${link.readingTime} min` : ""}
          </small>
          <div className="btn-group">
            <a
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="btn btn-sm btn-primary"
            >
              Open
            </a>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => navigator.clipboard?.writeText(link.url)}
            >
              Copy link
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-5">
      <div className="display-6 mb-2">No results</div>
      <p className="text-body-secondary">
        Try clearing filters or searching a different keyword.
      </p>
    </div>
  );
}
